import { makeAutoObservable } from 'mobx';

/**
 * Project Bobcat guided setup (on when the master, Validate and Observe are on) — which solutions are in guided
 * setup, and the furthest stage each has reached (an index into
 * `SETUP_STAGES`). Kept per browser in localStorage, like the other
 * per-designer Bobcat state, so a reload keeps your place. Started by
 * Create; ended by Finish or Exit.
 */
const STORAGE_KEY = 'bobcat-guided-setup';

const read = (): Record<string, number> => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : {};
    return parsed && typeof parsed === 'object'
      ? (parsed as Record<string, number>)
      : {};
  } catch {
    return {};
  }
};

export class GuidedSetupStore {
  private reachedBySolution: Record<string, number> = read();

  constructor() {
    makeAutoObservable(this);
  }

  private save() {
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(this.reachedBySolution)
      );
    } catch {
      // Storage full or blocked: setup still runs for this session.
    }
  }

  /** The furthest stage reached, or null when not in setup. */
  reachedFor(uniqueId: string | undefined): number | null {
    if (!uniqueId) return null;
    const reached = this.reachedBySolution[uniqueId];
    return typeof reached === 'number' ? reached : null;
  }

  start(uniqueId: string) {
    this.reachedBySolution[uniqueId] = 0;
    this.save();
  }

  /** Reaching a stage, by the coach or any other way in. */
  reach(uniqueId: string, index: number) {
    const reached = this.reachedFor(uniqueId);
    if (reached === null || index <= reached) return;
    this.reachedBySolution[uniqueId] = index;
    this.save();
  }

  end(uniqueId: string) {
    delete this.reachedBySolution[uniqueId];
    this.save();
  }
}

export const guidedSetupStore = new GuidedSetupStore();
